// src/api/badges.ts
import { API_BASE, buildHeaders, fetchWithAuth } from "../utils/api";
import { BADGES as DEFAULT_BADGES, type BadgeCode } from "../utils/xp";
import { get as storeGet, put as storePut, compositeKey } from "../utils/store";

export type Badge = {
  code: BadgeCode;
  label: string;
  threshold: number;
  minDay: number;
  image?: string | null;
};

export type BadgeAward = {
  code: BadgeCode;
  label: string;
  awardedAt: string; // ISO
};

type RawCatalogItem = {
  code: string;
  label: string;
  threshold: number;
  min_day: number;
  sort: number | null;
  image: string | null;
};

type RawAwardItem = {
  badge_code: string;
  awarded_at: string;
};

type BadgesResponse = {
  ok: boolean;
  catalog: RawCatalogItem[];
  awarded: RawAwardItem[];
};

// M5-STEP3B (16.09.2026): статус запроса по образцу SnapshotResult (snapshot.ts) —
// сетевой отказ отдельно от легитимно пустого каталога. Урок захода 3А: нельзя
// строить трёхзначное решение на бинарном поле — раньше `ok` в EMPTY_RESPONSE
// схлопывал оба случая, фолбэк на DEFAULT_BADGES маскировал сетевой отказ.
// M5-STORE2 (16.09.2026): добавлен "cached" — каталог и награды приходят ОДНИМ
// сетевым вызовом (/api/badges), поэтому кэшируются ОДНОЙ записью на курс —
// loadBadgesCatalog и loadAwarded читают общий кэш, не дублируют его.
type BadgesFetchResult =
  | { status: "ok"; data: BadgesResponse }
  | { status: "unavailable" }
  | { status: "cached"; data: BadgesResponse; cachedAt: number; stale: boolean };

/** Спросить кэш при сетевом отказе. Пусто в кэше → unavailable, как раньше. */
async function cachedOrUnavailable(key: string): Promise<BadgesFetchResult> {
  const cached = await storeGet<BadgesResponse>(key);
  if (cached.status === "empty") return { status: "unavailable" };
  console.error(`[badges] served from cache, age=${Date.now() - cached.cachedAt}`);
  return {
    status: "cached",
    data: cached.data,
    cachedAt: cached.cachedAt,
    stale: cached.status === "stale",
  };
}

/** Один сетевой вызов /api/badges — источник и для каталога, и для выданных наград */
async function getBadgesResponse(courseId?: number | string): Promise<BadgesFetchResult> {
  const key = compositeKey("badges", courseId ?? "none");

  let res: Response;
  try {
    const qs = courseId != null ? `?course_id=${encodeURIComponent(String(courseId))}` : "";
    res = await fetchWithAuth(`${API_BASE}/api/badges${qs}`, {
      headers: buildHeaders(false),
    });
  } catch (e) {
    console.error("[badges] network error:", e instanceof Error ? e.message : String(e));
    return cachedOrUnavailable(key);
  }

  if (!res.ok) {
    console.error("[badges] error status:", res.status);
    return cachedOrUnavailable(key);
  }

  try {
    const json = await res.json();
    const data: BadgesResponse = {
      ok: !!json?.ok,
      catalog: Array.isArray(json?.catalog) ? json.catalog : [],
      awarded: Array.isArray(json?.awarded) ? json.awarded : [],
    };
    // нет локального источника user_id в этом модуле — см. content.ts/progress.ts,
    // тот же довод: clearAll() при смене пользователя уже накрывает эти ключи.
    await storePut(key, data, 0);
    return { status: "ok", data };
  } catch (e) {
    console.error("[badges] 200 but json parse failed:", e instanceof Error ? e.message : String(e));
    return cachedOrUnavailable(key);
  }
}

// QUEUE-UI-B (22.09.2026), Шаг 4: сервер отбирает выданный бейдж проходом
// по каталогу в порядке `sort=sort,threshold` (api/index.js) и оставляет
// ПОСЛЕДНЕЕ подходящее совпадение — при нескольких порогах, пройденных
// одним начислением разом, побеждает последний по этому порядку, не по
// threshold отдельно. Раньше клиент сортировал только по threshold — на
// сегодняшних данных (sort пуст у всех записей каталога) результат
// совпадает с серверным случайно, не по гарантии; при заполненном sort,
// не совпадающем с порядком threshold, клиентский прогноз (badges.ts —
// эта функция — и qualifyingBadgeFrom, DashboardScreen) выбрал бы другой
// бейдж, чем реально начислит сервер. NULLS LAST — тот же порядок, что
// Postgres/Directus дают по умолчанию для `ORDER BY sort ASC` (SQL-стандарт),
// пустые записи `sort` уходят в конец, сортируются между собой по threshold.
function compareCatalogOrder(a: RawCatalogItem, b: RawCatalogItem): number {
  const sa = a?.sort;
  const sb = b?.sort;
  if (sa == null && sb == null) return Number(a?.threshold ?? 0) - Number(b?.threshold ?? 0);
  if (sa == null) return 1;
  if (sb == null) return -1;
  if (sa !== sb) return sa - sb;
  return Number(a?.threshold ?? 0) - Number(b?.threshold ?? 0);
}

function mapCatalog(rows: RawCatalogItem[]): Badge[] {
  return [...rows]
    .sort(compareCatalogOrder)
    .map((r) => ({
      code: String(r?.code ?? "").trim() as BadgeCode,
      label: String(r?.label ?? "").trim(),
      threshold: Number(r?.threshold ?? 0),
      minDay: Number(r?.min_day ?? 0),
      image: r?.image ? String(r.image) : null,
    }))
    .filter((b) => !!b.code && !!b.label && Number.isFinite(b.threshold) && b.threshold > 0);
}

function mapDefault(): Badge[] {
  return DEFAULT_BADGES.map((b) => ({
    code: b.code,
    label: b.label,
    threshold: b.threshold,
    minDay: b.minDay,
    image: null,
  }));
}

// На экспорт — удобный фолбэк, если нужен
export const DEFAULT_BADGES_MAPPED: Badge[] = mapDefault();

// "ok" — каталог автора непустой; "empty" — каталог пуст (легитимно, автор не
// завёл свои бейджи — фолбэк DEFAULT_BADGES законен); "unavailable" — не
// удалось спросить, фолбэк НЕ подставляется. "cached" — не удалось спросить,
// но есть последний известный каталог/награды (content-access, инвариант 9).
export type BadgesCatalogResult =
  | { status: "ok"; data: Badge[] }
  | { status: "empty"; data: Badge[] }
  | { status: "unavailable" }
  | { status: "cached"; data: Badge[]; cachedAt: number; stale: boolean };

/** Тянем активные бейджи для курса. Пустой каталог → фолбэк DEFAULT_BADGES (легитимно); сбой сети → фолбэк НЕ подставляется. */
export async function loadBadgesCatalog(courseId?: number | string): Promise<BadgesCatalogResult> {
  const resp = await getBadgesResponse(courseId);
  if (resp.status === "unavailable") return { status: "unavailable" };

  const mapped = mapCatalog(resp.data.catalog);
  if (resp.status === "cached") {
    if (mapped.length === 0) return { status: "empty", data: DEFAULT_BADGES_MAPPED };
    return { status: "cached", data: mapped, cachedAt: resp.cachedAt, stale: resp.stale };
  }
  if (mapped.length === 0) return { status: "empty", data: DEFAULT_BADGES_MAPPED };
  return { status: "ok", data: mapped };
}

export type AwardedResult =
  | { status: "ok"; data: BadgeAward[] }
  | { status: "unavailable" }
  | { status: "cached"; data: BadgeAward[]; cachedAt: number; stale: boolean };

/** Список уже выданных наград по курсу; label в awarded[] с сервера не приходит — матчим по code из каталога */
export async function loadAwarded(courseId?: number | string): Promise<AwardedResult> {
  const resp = await getBadgesResponse(courseId);
  if (resp.status === "unavailable") return { status: "unavailable" };

  const labelByCode = new Map<string, string>();
  for (const r of resp.data.catalog) {
    labelByCode.set(String(r?.code ?? "").trim(), String(r?.label ?? "").trim());
  }

  const data = resp.data.awarded
    .map((r) => {
      const code = String(r?.badge_code ?? "").trim() as BadgeCode;
      const awardedAt = String(r?.awarded_at ?? "").trim();
      return { code, label: labelByCode.get(code) ?? "", awardedAt };
    })
    .filter((a) => !!a.code && !!a.awardedAt)
    .sort((a, b) => new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime());

  if (resp.status === "cached") {
    return { status: "cached", data, cachedAt: resp.cachedAt, stale: resp.stale };
  }
  return { status: "ok", data };
}
